import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { Link, useBlocker, useParams, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, ChevronDown, Eye, ExternalLink, History as HistoryIcon, Layers, Monitor, MoreHorizontal, PenLine, Plus, Redo2, Rocket, Save, Smartphone, Tablet, Undo2, X } from "lucide-react";
import { toast } from "sonner";
import { WebsiteRenderer } from "@baze/engine";
import type { Draft, PublishIssue } from "@baze/api-client";
import { SECTION_META, type SectionType, type WebsiteDefinition } from "@baze/schema";
import { Button, buttonClass } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Segmented } from "@/components/ui/segmented";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/States";
import { ApiError, api } from "@/lib/api";
import { qk } from "@/lib/query";
import { findTemplate, industryName, themeFor } from "@/lib/catalog";
import { useCatalog, useDraft, useSite } from "@/lib/queries";
import { cn } from "@/lib/cn";
import { useIsDesktop, useMediaQuery } from "@/lib/hooks";
import { local, session } from "@/lib/storage";
import { t } from "@/i18n/id";
import { useDocumentTitle } from "@/lib/document-title";
import { displayUrl } from "@/features/sites/SiteStatus";
import { DeviceFrame, type Device } from "@/features/preview/DeviceFrame";
import { handlePreviewLink } from "@/features/preview/preview-links";
import { SiteRuntime } from "@/features/preview/SiteRuntime";
import { Inspector } from "./inspector";
import { InlineTextEditor, textSpec, type InlineTarget } from "./inline-edit";
import { itemPath } from "./workspace/specs";
import { ResizeHandle } from "./resize-handle";
import { Outline } from "./outline";
import { AddSectionSheet, DockedTextEditor, SaveChip, SectionActionBar, SheetHandle, sheetClasses, type SheetSize } from "./mobile";
import { PublishDialog } from "./PublishDialog";
import { TextToolbar, type TextTarget } from "./style/TextToolbar";
import { addPage, addSection, duplicateSection, locateSection, moveSection, removePage, removeSection, renamePage, reorderSections, setHidden, setPageInNav, updateContent, type PagePreset } from "./model/ops";
import { getPath } from "./model/paths";
import type { DefaultsContext } from "./model/defaults";
import { commit, createHistory, redo, undo, type History } from "./model/history";
import { VerifyEmailBanner } from "@/components/shell/VerifyEmailBanner";
import { EVENT_INDUSTRIES } from "@/features/sites/overview/context";
import { useStorePreview } from "@/features/store/preview";

type SheetName = "outline" | "inspect" | "add" | null;

/**
 * Bring a section into the canvas scroller only. `scrollIntoView` also walks every ancestor and
 * honours the site's large scroll-margin, so a click slid the page. Already-visible sections stay put.
 */
function revealSection(root: HTMLElement | null, sectionId: string, toStart: boolean) {
  if (!root) return;
  const el = root.querySelector<HTMLElement>(`[data-baze-section="${CSS.escape(sectionId)}"]`);
  const scroller = root.closest<HTMLElement>(".overflow-auto");
  if (!el || !scroller) return;
  const er = el.getBoundingClientRect();
  const sr = scroller.getBoundingClientRect();
  const nav = root.querySelector(".baze-navbar")?.getBoundingClientRect().height ?? 0;
  const topGap = Math.min(nav, 88) + 8;
  const fullyIn = er.top >= sr.top + (toStart ? 0 : topGap) - 4 && er.bottom <= sr.bottom - 8;
  const tallAndTopVisible = er.height > sr.height - topGap && er.top >= sr.top && er.top <= sr.top + topGap + 12;
  if (!toStart && (fullyIn || tallAndTopVisible)) return;
  const delta = toStart || er.top < sr.top + topGap || er.height > sr.height - topGap ? er.top - sr.top - (toStart ? 4 : topGap) : er.bottom - sr.bottom + 8;
  if (Math.abs(delta) < 2) return;
  scroller.scrollBy({ top: delta, behavior: "auto" });
}

/** Quiet period after the last edit before the draft is saved automatically. */
const AUTOSAVE_MS = 1500;
/** Remembers whether the inspector is the wide panel, and its width in each mode. */
const WIDE_KEY = "baze:editor-wide";
const WIDTH_KEY = { normal: "baze:editor-inspector:normal", wide: "baze:editor-inspector:wide" } as const;
const NORMAL_WIDTH = 340;
/** Default wide width: what's left after the preview's share, up to a width that still reads well. */
const WIDE_DEFAULT_MAX = 820;
const CANVAS_ROOMY = 480;
/** Page outline on the left. */
const OUTLINE_WIDTH = 272;
/** Room the preview always keeps next to the inspector. */
const CANVAS_MIN = 360;
/** Dragging the normal panel past this switches to the wide panel; the wide one below WIDE_EXIT switches back. */
const WIDE_ENTER = 560;
const WIDE_EXIT = 440;
/** Narrower than this, the wide panel's preview shows the phone frame. */
const CANVAS_DESKTOP_MIN = 800;

/**
 * The list item under a click in the canvas, from the engine's `data-field` paths: the deepest
 * numbered segment the fields around the click share ("content.groups.0.topics.1.title" and
 * "…topics.1.summary" → groups/0/topics/1). Null outside any item.
 */
function itemAt(target: Element, section: Element): string[] | null {
  for (let el: Element | null = target; el && el !== section; el = el.parentElement) {
    const fields = [...(el.matches("[data-field]") ? [el] : []), ...el.querySelectorAll("[data-field]")].map((f) => (f.getAttribute("data-field") ?? "").split("."));
    if (!fields.length) continue;
    let common = fields[0]!;
    for (const f of fields) {
      let i = 0;
      while (i < common.length && common[i] === f[i]) i++;
      common = common.slice(0, i);
    }
    const last = common.findLastIndex((seg) => /^\d+$/.test(seg));
    return last < 0 ? null : common.slice(common[0] === "content" ? 1 : 0, last + 1);
  }
  return null;
}

export default function EditorPage() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const draft = useDraft(id);
  const site = useSite(id);
  const catalog = useCatalog();
  const qc = useQueryClient();
  const [history, setHistory] = useState<History<WebsiteDefinition> | null>(null);
  const [revision, setRevision] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [pageId, setPageId] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>(() => (window.innerWidth < 1024 ? "mobile" : "desktop"));
  const [sheet, setSheetState] = useState<SheetName>(null);
  const [sheetSize, setSheetSize] = useState<SheetSize>("half");
  const setSheet = useCallback((next: SheetName | ((cur: SheetName) => SheetName)) => {
    setSheetState(next);
    setSheetSize("half");
  }, []);
  // Phones and tablets: panels are bottom sheets, text is typed in a bar docked above the keyboard.
  const isDesktop = useIsDesktop();
  const touch = useMediaQuery("(pointer: coarse)");
  const [previewMode, setPreviewMode] = useState(false);
  // Bumped by "Edit isi" so the inspector jumps to the Section tab even if another tab was open.
  const [inspectNonce, setInspectNonce] = useState(0);
  // Bumped to open the Info tab (site details: WhatsApp, contact), e.g. from a publish warning.
  const [infoNonce, setInfoNonce] = useState(0);
  const bannerKey = `baze:editor-banner:${id}`;
  const [bannerHidden, setBannerHidden] = useState(() => session.get(bannerKey, false));
  const [bannerOpen, setBannerOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [inline, setInline] = useState<InlineTarget | null>(null);
  // The text whose style the header toolbar edits (outlives the in-place editor until the next canvas click, Escape, or Close).
  const [textTarget, setTextTarget] = useState<TextTarget | null>(null);
  const [widePref, setWidePref] = useState(() => local.get(WIDE_KEY, false));
  // Wide panel on a screen too narrow to keep the outline docked: the outline opens over the preview.
  const [outlineOverlay, setOutlineOverlay] = useState(false);
  const [widths, setWidths] = useState(() => ({ normal: local.get(WIDTH_KEY.normal, NORMAL_WIDTH), wide: local.get(WIDTH_KEY.wide, 0) }));
  // Width while the edge is being dragged (stored when the drag ends).
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const [bodyWidth, setBodyWidth] = useState(0);
  // The panel animates its width only after the first measured layout, not on the way in.
  const [measured, setMeasured] = useState(false);
  useEffect(() => {
    if (bodyWidth > 0 && !measured) {
      const frame = requestAnimationFrame(() => setMeasured(true));
      return () => cancelAnimationFrame(frame);
    }
  }, [bodyWidth, measured]);
  const bodyObserver = useRef<ResizeObserver | null>(null);
  const setBody = useCallback((el: HTMLDivElement | null) => {
    bodyObserver.current?.disconnect();
    if (!el) return;
    bodyObserver.current = new ResizeObserver(([entry]) => setBodyWidth(Math.round(entry!.contentRect.width)));
    bodyObserver.current.observe(el);
  }, []);
  // The list item the inspector should show: from a click on it in the preview, or the workspace tree.
  const [focusItem, setFocusItem] = useState<{ sectionId: string; path: string[] | null; nonce: number } | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  // The inline editor measures against the canvas element, so it is also kept in state.
  const [canvasEl, setCanvasEl] = useState<HTMLDivElement | null>(null);
  const setCanvas = useCallback((el: HTMLDivElement | null) => {
    canvasRef.current = el;
    setCanvasEl(el);
  }, []);
  const hoverRef = useRef<string | null>(null);
  const revisionKey = draft.data ? `${id}:${draft.data.revision}` : "";
  const [loadedKey, setLoadedKey] = useState("");
  const [baseline, setBaseline] = useState("");
  if (draft.data && revisionKey !== loadedKey) {
    setLoadedKey(revisionKey);
    setBaseline(JSON.stringify(draft.data.definition));
    setHistory(createHistory(draft.data.definition));
    setRevision(draft.data.revision);
    setPageId((current) => current ?? draft.data?.definition.pages[0]?.id ?? null);
  }

  const def = history?.present;
  useDocumentTitle(site.data?.name ?? def?.site.name, t.overview.edit);
  const page = def?.pages.find((p) => p.id === pageId) ?? def?.pages[0];
  const storePreview = useStorePreview(id, page);
  const section = selected && def ? locateSection(def, selected)?.section : undefined;
  // The wide panel is a desktop layout; phones keep their sheets.
  const wide = widePref && isDesktop;
  const setWide = useCallback((on: boolean) => {
    setWidePref(on);
    setOutlineOverlay(false);
    local.set(WIDE_KEY, on);
  }, []);
  // The wide panel keeps the outline docked when the preview still gets its room next to both.
  const outlineDocked = !wide || bodyWidth >= OUTLINE_WIDTH + WIDE_ENTER + CANVAS_MIN;
  const minWidth = wide ? 400 : 300;
  const maxWidth = Math.max(minWidth, bodyWidth - (outlineDocked ? OUTLINE_WIDTH : 0) - CANVAS_MIN);
  // The normal panel may be dragged on up to the point where it turns into the wide one.
  const dragMax = wide ? maxWidth : Math.max(maxWidth, WIDE_ENTER);
  const panelWidth = Math.round(Math.min(dragWidth === null ? maxWidth : dragMax, Math.max(minWidth, dragWidth ?? (wide ? widths.wide || Math.min(WIDE_DEFAULT_MAX, Math.max(WIDE_ENTER, bodyWidth - (outlineDocked ? OUTLINE_WIDTH : 0) - CANVAS_ROOMY)) : widths.normal))));
  // A preview squeezed by the wide panel shows the phone frame (and no device switch).
  const phoneOnly = wide && bodyWidth - (outlineDocked ? OUTLINE_WIDTH : 0) - panelWidth < CANVAS_DESKTOP_MIN;
  /** 0 for the wide panel means "its default share of the editor". */
  const storeWidth = (mode: "normal" | "wide", width: number) => {
    setWidths((cur) => ({ ...cur, [mode]: width }));
    local.set(WIDTH_KEY[mode], width);
  };
  /** End of a drag or a key press on the edge: keep the width, switching panels past the thresholds. */
  const commitWidth = (width: number) => {
    setDragWidth(null);
    if (!wide && width >= WIDE_ENTER) {
      storeWidth("wide", width);
      setWide(true);
    } else if (wide && width <= WIDE_EXIT) {
      storeWidth("normal", width);
      setWide(false);
    } else storeWidth(wide ? "wide" : "normal", width);
  };
  /** Point the inspector at the list item under a preview click (a new nonce opens it again). */
  const focusOn = (sectionId: string, target: Element, node: Element) => {
    const type = def ? locateSection(def, sectionId)?.section.type : undefined;
    const raw = itemAt(target, node);
    const path = type && raw ? itemPath(type, raw) : null;
    setFocusItem((cur) => ({ sectionId, path, nonce: (cur?.nonce ?? 0) + 1 }));
  };
  const theme = useMemo(() => (def && catalog.data ? themeFor(catalog.data, def.theme) : null), [def, catalog.data]);
  // New sections and pages start from the site's own template content where it has some.
  const templateId = def?.meta?.templateId;
  const industry = def?.site.industry;
  const defaults = useMemo<DefaultsContext>(() => {
    const c = catalog.data;
    if (!c) return {};
    return {
      template: findTemplate(c, templateId) as DefaultsContext["template"],
      stock: c.stockImages,
      anchors: c.sectionAnchors,
      industryName: industry ? industryName(c, industry) : undefined,
    };
  }, [catalog.data, templateId, industry]);
  // "Tambah section" leads with what this kind of site's templates use (RSVP and Hadiah for a wedding).
  const suggested = useMemo(() => {
    const types = new Set<SectionType>();
    for (const tpl of catalog.data?.templates ?? []) {
      if (tpl.industry !== industry) continue;
      for (const p of tpl.pages) for (const s of p.sections) if (s.type !== "navbar" && s.type !== "footer") types.add(s.type);
    }
    return [...types];
  }, [catalog.data, industry]);
  const event = !!industry && EVENT_INDUSTRIES.has(industry);
  // Artikel Terbaru and the live blog exist only on plans that include them.
  const blog = !!site.data && site.data.plan.limits.blogPosts > 0 && !event;
  const dirty = !!def && JSON.stringify(def) !== baseline;
  const live = site.data?.status === "published";
  const pending = live && (dirty || !!site.data?.hasUnpublishedChanges);
  // What the publish check would flag, per section, kept fresh as the draft is saved.
  const check = useQuery({ queryKey: [...qk.sites.publishCheck(id), "rev", revision], queryFn: () => api.sites.publishCheck(id), enabled: revision > 0, staleTime: 30_000 });
  const issues = useMemo(() => {
    const by = new Map<string, PublishIssue[]>();
    for (const issue of [...(check.data?.blockers ?? []), ...(check.data?.warnings ?? [])]) {
      if (issue.sectionId) by.set(issue.sectionId, [...(by.get(issue.sectionId) ?? []), issue]);
    }
    return by;
  }, [check.data]);

  const save = useMutation({
    mutationFn: async (definition: WebsiteDefinition) => {
      const res = await api.sites.saveDraft(id, definition, revision);
      return { res, definition };
    },
    meta: { silent: true },
    onSuccess: ({ res, definition }) => {
      setLoadedKey(`${id}:${res.revision}`);
      setBaseline(JSON.stringify(definition));
      setRevision(res.revision);
      qc.setQueryData<Draft>(qk.sites.draft(id), (old) => (old ? { ...old, definition, revision: res.revision, updatedAt: res.updatedAt } : old));
      void qc.invalidateQueries({ queryKey: qk.sites.detail(id) });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) void draft.refetch();
      else toast.error(t.editor.save.error);
    },
  });

  const apply = (next: WebsiteDefinition, key?: string) => {
    setHistory((h) => (h ? commit(h, next, key) : h));
  };

  const saveNow = useCallback(async () => {
    if (!def || JSON.stringify(def) === baseline) return;
    await save.mutateAsync(def);
  }, [baseline, def, save]);

  const select = (sectionId: string, scroll: boolean) => {
    setSelected(sectionId);
    if (inline && sectionId !== inline.sectionId) setInline(null);
    if (textTarget && sectionId !== textTarget.sectionId) setTextTarget(null);
    const loc = def ? locateSection(def, sectionId) : null;
    if (loc) setPageId(loc.page.id);
    if (scroll) {
      requestAnimationFrame(() => {
        // Below lg a sheet may cover the lower half: bring the section to the top of the canvas.
        revealSection(canvasRef.current, sectionId, !isDesktop);
      });
    }
  };

  /** Site details (WhatsApp, contact) in the inspector's Info tab; a sheet on phones. */
  const openInfo = () => {
    setSelected(null);
    setInline(null);
    setTextTarget(null);
    setInfoNonce((n) => n + 1);
    if (!isDesktop) setSheet("inspect");
  };

  /** Open the inspector on the selected section (mobile "Edit isi"). */
  const editSelected = () => {
    setInspectNonce((n) => n + 1);
    setSheet("inspect");
  };

  /** Delete with an "Urungkan" toast; undo only applies while nothing else has changed since. */
  const deleteSection = useCallback(
    (sectionId: string) => {
      if (!def) return;
      const loc = locateSection(def, sectionId);
      if (!loc || loc.section.type === "navbar" || loc.section.type === "footer") return;
      const next = removeSection(def, sectionId);
      setHistory((h) => (h ? commit(h, next) : h));
      setSelected((cur) => (cur === sectionId ? null : cur));
      setInline((cur) => (cur?.sectionId === sectionId ? null : cur));
      setTextTarget((cur) => (cur?.sectionId === sectionId ? null : cur));
      toast(t.editor.outline.deleted(typeLabel(loc.section.type)), {
        action: { label: t.editor.undo, onClick: () => setHistory((h) => (h && h.present === next ? undo(h) : h)) },
      });
    },
    [def],
  );

  const duplicate = (sectionId: string) => {
    if (!def) return;
    const res = duplicateSection(def, sectionId);
    if (!res.id) return;
    apply(res.def);
    select(res.id, true);
  };

  const publishFromUrl = params.get("publish") === "1";

  // Deep links from the site overview: ?section=<id> opens that section, ?tab=info the site details
  // (WhatsApp and contact). On phones the inspector lives in a sheet, so open it.
  const linkedSection = params.get("section");
  const linkedInfo = params.get("tab") === "info";
  const loaded = !!def;
  useEffect(() => {
    if (!loaded || (!linkedSection && !linkedInfo)) return;
    // After the canvas has painted, so the section can scroll into view.
    const frame = requestAnimationFrame(() => {
      if (linkedSection) select(linkedSection, true);
      else openInfo();
      if (!isDesktop) setSheet("inspect");
      const cleared = new URLSearchParams(params);
      cleared.delete("section");
      cleared.delete("tab");
      setParams(cleared, { replace: true });
    });
    return () => cancelAnimationFrame(frame);
    // Runs once per link; select/params are fresh on that render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, linkedSection, linkedInfo]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      const typing = e.target instanceof HTMLElement && (e.target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName));
      if (meta && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (dirty) void saveNow();
        return;
      }
      if (meta && e.key === "\\") {
        e.preventDefault();
        setWide(!widePref);
        return;
      }
      if (typing) return;
      // Esc ends text styling first, then leaves the wide panel, then clears the selection.
      if (e.key === "Escape") {
        if (document.querySelector('[role="dialog"]')) return;
        if (textTarget) {
          setTextTarget(null);
          setInline(null);
          return;
        }
        if (outlineOverlay) setOutlineOverlay(false);
        else if (widePref) setWide(false);
        else setSelected(null);
      }
      if ((e.key === "Delete" || e.key === "Backspace") && selected && def) {
        e.preventDefault();
        deleteSection(selected);
      }
      if (e.altKey && (e.key === "ArrowUp" || e.key === "ArrowDown") && selected && def) {
        e.preventDefault();
        apply(moveSection(def, selected, e.key === "ArrowUp" ? -1 : 1));
      }
      if (meta && e.key.toLowerCase() === "z") {
        e.preventDefault();
        setHistory((h) => (h ? (e.shiftKey ? redo(h) : undo(h)) : h));
      }
      if (meta && e.key.toLowerCase() === "y") {
        e.preventDefault();
        setHistory((h) => (h ? redo(h) : h));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dirty, selected, def, revision, saveNow, deleteSection, widePref, setWide, outlineOverlay, textTarget]);

  // Lets global styles (toast position) adapt while the editor is open.
  useEffect(() => {
    document.body.setAttribute("data-editor", "");
    return () => document.body.removeAttribute("data-editor");
  }, []);

  useEffect(() => {
    if (!dirty) return;
    const onLeave = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirty]);

  // Autosave: shortly after the last edit. A failed save is not retried until the draft changes again
  // (the header shows the error and the Save button stays available).
  useEffect(() => {
    if (!def || !dirty || save.isPending || (save.isError && save.variables === def)) return;
    const timer = window.setTimeout(() => void saveNow().catch(() => undefined), AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
  }, [def, dirty, save.isPending, save.isError, save.variables, saveNow]);

  // Leaving with unsaved edits (e.g. right after typing): save first, ask only if that fails.
  const blocker = useBlocker(dirty);
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    saveNow().then(
      () => blocker.proceed(),
      () => {
        if (window.confirm(t.editor.leaveWarning)) blocker.proceed();
        else blocker.reset();
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per blocked navigation
  }, [blocker.state]);

  const clearHover = () => {
    const root = canvasRef.current;
    if (root && hoverRef.current) root.querySelector(`[data-baze-section="${CSS.escape(hoverRef.current)}"]`)?.removeAttribute("data-hovered");
    hoverRef.current = null;
  };

  const onCanvasOver = (e: MouseEvent<HTMLDivElement>) => {
    if (previewMode) return;
    const node = (e.target as HTMLElement).closest("[data-baze-section]");
    const next = node?.getAttribute("data-baze-section") ?? null;
    if (next === hoverRef.current) return;
    clearHover();
    hoverRef.current = next;
    if (next) node?.setAttribute("data-hovered", "");
  };

  /** Start editing a text field element in place. False when it is not plain editable text. */
  const startInline = (fieldEl: HTMLElement): boolean => {
    if (!def) return false;
    const sectionId = fieldEl.closest("[data-baze-section]")?.getAttribute("data-baze-section");
    const path = fieldEl.getAttribute("data-field");
    const loc = sectionId ? locateSection(def, sectionId) : null;
    const spec = loc && path ? textSpec(loc.section.type, path) : null;
    const current = loc && path ? getPath(loc.section, path) : undefined;
    if (!sectionId || !path || !spec || typeof current !== "string") return false;
    select(sectionId, false);
    setInline({ sectionId, path, initial: current, spec });
    setTextTarget({ sectionId, path });
    // Touch: the docked bar and keyboard cover the lower screen; keep the text in view above them.
    if (touch) requestAnimationFrame(() => revealSection(canvasRef.current, sectionId, true));
    return true;
  };

  /** Tab / Shift+Tab inside the in-place editor: the next editable text in page order, else close. */
  const moveInline = (dir: 1 | -1) => {
    const root = canvasRef.current;
    if (!root || !inline) return setInline(null);
    const all = [...root.querySelectorAll<HTMLElement>("[data-baze-section] [data-field]")].filter((el) => el.getClientRects().length > 0);
    const at = all.findIndex((el) => el.getAttribute("data-field") === inline.path && el.closest("[data-baze-section]")?.getAttribute("data-baze-section") === inline.sectionId);
    for (let i = at + dir; i >= 0 && i < all.length; i += dir) {
      const el = all[i]!;
      if (startInline(el)) {
        const id = el.closest("[data-baze-section]")?.getAttribute("data-baze-section");
        if (id) revealSection(root, id, false);
        return;
      }
    }
    setInline(null);
  };

  const onCanvasDown = (e: MouseEvent<HTMLDivElement>) => {
    if (previewMode) return;
    const el = e.target as HTMLElement;
    // Focusing a site link or button scrolls it into view. Keep the click, drop the focus scroll.
    if (el.closest("input, textarea, select, [contenteditable='true']")) return;
    e.preventDefault();
  };

  const onCanvasClick = (e: MouseEvent<HTMLDivElement>) => {
    // Pratinjau: links behave as for visitors, inside the editor.
    if (previewMode) {
      handlePreviewLink(e, def, (slug) => {
        const next = def?.pages.find((p) => p.slug === slug);
        if (next) setPageId(next.id);
        const scroller = canvasRef.current?.closest<HTMLElement>(".overflow-auto");
        if (scroller) scroller.scrollTop = 0;
      });
      return;
    }
    const el = e.target as HTMLElement;
    if (el.closest(".baze-inline-editor, .baze-text-toolbar")) return;
    // Capture phase: stop the site's own menus, accordions and buttons before they open and shift the page.
    const interactive = el.closest("a, button, summary");
    if (interactive) {
      e.preventDefault();
      e.stopPropagation();
    }
    const node = el.closest("[data-baze-section]");
    const sectionId = node?.getAttribute("data-baze-section");
    // Any canvas click retargets the text toolbar (editing a text sets it again below).
    setTextTarget(null);
    if (!sectionId || !def) return;
    // Text marked by the engine is edited right where it is; anything else opens the inspector.
    const fieldEl = el.closest<HTMLElement>("[data-field]");
    if (fieldEl && node?.contains(fieldEl) && startInline(fieldEl)) {
      // FAQ questions sit in <summary>: edit the text instead of toggling the answer.
      if (el.closest("summary")) e.preventDefault();
      focusOn(sectionId, el, node!);
      return;
    }
    select(sectionId, false);
    if (node) focusOn(sectionId, el, node);
  };

  if (draft.isPending || catalog.isPending || !history || !def || !page) {
    if (draft.isError || catalog.isError) return <ErrorState error={draft.error ?? catalog.error} onRetry={() => void draft.refetch()} />;
    return <Skeleton className="m-6 h-[70vh] rounded-2xl" />;
  }

  const url = site.data?.primaryUrl;
  /** Insert right after the selected section on this page; otherwise before the footer. */
  const addOfType = (type: SectionType) => {
    const at = selected ? page.sections.findIndex((s) => s.id === selected) : -1;
    const res = addSection(def, page.id, type, at >= 0 ? at + 1 : undefined, defaults);
    if (!res.id) return;
    apply(res.def);
    if (!isDesktop) setSheet(null);
    select(res.id, true);
    toast.success(t.editor.picker.added(typeLabel(type)));
  };
  const inlineSection = inline ? locateSection(def, inline.sectionId)?.section : undefined;
  const inlineValue = inlineSection && inline ? (getPath(inlineSection, inline.path) as string | undefined) : undefined;
  const outline = (
    <Outline
      siteId={id}
      def={def}
      pageId={page.id}
      pageLimit={site.data?.plan.limits.pagesPerSite}
      sections={page.sections}
      selected={selected}
      onPage={(next) => { setPageId(next); setSelected(null); setInline(null); setTextTarget(null); }}
      onAddPage={(preset: PagePreset, title: string, inNav: boolean) => {
        const res = addPage(def, preset, title, defaults, inNav);
        apply(res.def);
        setPageId(res.pageId);
        setSelected(null);
        setInline(null);
        setTextTarget(null);
        toast.success(t.editor.pageAdded(title));
      }}
      onRenamePage={(pageId, title) => apply(renamePage(def, pageId, title))}
      onTogglePageNav={(pageId, show) => {
        const target = def.pages.find((p) => p.id === pageId);
        if (!target) return;
        apply(setPageInNav(def, pageId, show));
        toast(show ? t.editor.pageShownInNav(target.title) : t.editor.pageHiddenFromNav(target.title));
      }}
      onRemovePage={(pageId) => {
        const removed = def.pages.find((p) => p.id === pageId);
        const next = removePage(def, pageId);
        if (!removed || next === def) return;
        apply(next);
        setSelected(null);
        setInline(null);
        setTextTarget(null);
        setPageId(next.pages.find((p) => p.slug === "/")?.id ?? next.pages[0]?.id ?? null);
        toast(t.editor.pageRemoved(removed.title), {
          action: {
            label: t.editor.undo,
            onClick: () => {
              setHistory((h) => (h && h.present === next ? undo(h) : h));
              setPageId(pageId);
            },
          },
        });
      }}
      onReorder={(from, to) => apply(reorderSections(def, page.id, from, to))}
      onSelect={(sectionId) => {
        select(sectionId, true);
        if (!isDesktop) setSheet(null);
        setOutlineOverlay(false);
      }}
      onAdd={addOfType}
      onMove={(sectionId, delta) => apply(moveSection(def, sectionId, delta))}
      onHide={(sectionId, hidden) => apply(setHidden(def, sectionId, hidden))}
      onDelete={deleteSection}
      issues={issues}
      suggested={suggested}
      event={event}
      blog={blog}
    />
  );
  const inspector = (
    <Inspector
      siteId={id}
      siteUrl={url}
      def={def}
      pageId={page.id}
      section={section}
      apply={apply}
      onMove={(sectionId, delta) => apply(moveSection(def, sectionId, delta))}
      onDuplicate={duplicate}
      onDelete={deleteSection}
      focusSection={inspectNonce}
      focusInfo={infoNonce}
      issues={section ? issues.get(section.id) : undefined}
      wide={wide}
      expanded={wide}
      onExpand={setWide}
      stacked={!isDesktop || panelWidth < 640}
      focusItem={focusItem && focusItem.sectionId === section?.id ? focusItem : null}
      onFocusItem={(path) => section && setFocusItem((cur) => ({ sectionId: section.id, path, nonce: cur?.nonce ?? 0 }))}
    />
  );

  const saveState = save.isPending ? "saving" : save.isError ? "error" : dirty ? "dirty" : "saved";
  const showBanner = !bannerHidden && ((!live && !!url) || pending);
  const showActionBar = !isDesktop && !!section && !sheet && !inline && !previewMode;
  const saveText = save.isPending ? t.editor.save.saving : save.isError ? t.editor.save.error : dirty ? t.editor.save.dirty : t.editor.save.savedNow;

  return (
    // overflow-clip, not hidden: scrollIntoView (canvas, inspector) must never scroll the shell itself.
    <div className="flex h-dvh flex-col overflow-clip bg-[#eef1f4]">
      <header className="safe-top z-40 flex h-14 shrink-0 items-center gap-1 border-b border-line bg-white px-2 sm:gap-2 sm:px-3">
        <Link to={`/situs/${id}`} className={buttonClass("ghost", "sm", "max-w-[34vw] max-sm:px-2.5 sm:max-w-[200px]")} aria-label={t.editor.back}>
          <ArrowLeft aria-hidden="true" />
          <span className="truncate max-sm:sr-only">{site.data?.name ?? t.editor.back}</span>
        </Link>
        <SaveChip state={saveState} onRetry={() => void saveNow().catch(() => undefined)} />
        {site.data ? (
          <Badge tone={live && !pending ? "success" : "warning"} dot className="max-sm:hidden">
            {!live ? t.editor.status.draft : pending ? t.editor.status.liveChanged : t.editor.status.live}
          </Badge>
        ) : null}
        <Button variant="ghost" size="icon-sm" disabled={!history.past.length} onClick={() => setHistory((h) => (h ? undo(h) : h))} aria-label={t.editor.undo}>
          <Undo2 aria-hidden="true" />
        </Button>
        <Button variant="ghost" size="icon-sm" className="max-sm:hidden" disabled={!history.future.length} onClick={() => setHistory((h) => (h ? redo(h) : h))} aria-label={t.editor.redo}>
          <Redo2 aria-hidden="true" />
        </Button>
        <p className={cn("hidden min-w-0 truncate text-xs font-medium md:block", save.isError ? "text-danger" : dirty ? "text-amber-700" : "text-slate-400")} aria-live="polite">
          {dirty ? null : <Check className="mr-1 inline size-3.5" aria-hidden="true" />}
          {saveText}
        </p>
        <div className="ml-auto flex items-center gap-1.5">
          {live && url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" className={buttonClass("ghost", "sm", "max-lg:hidden")}>
              <ExternalLink aria-hidden="true" />
              {displayUrl(url)}
            </a>
          ) : null}
          <Link to={`/situs/${id}/pratinjau`} className={buttonClass("ghost", "sm", "max-sm:hidden")}>
            <Eye aria-hidden="true" />
            {t.editor.preview}
          </Link>
          <Link to={`/situs/${id}/riwayat`} className={buttonClass("ghost", "icon-sm", "max-sm:hidden")} aria-label={t.editor.versions} title={t.editor.versions}>
            <HistoryIcon aria-hidden="true" />
          </Link>
          {dirty || save.isPending || save.isError ? (
            <Button variant="secondary" size="sm" className="max-sm:hidden" onClick={() => void saveNow()} disabled={!dirty || save.isPending} loading={save.isPending} leftIcon={<Save aria-hidden="true" />}>
              {t.common.save}
            </Button>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" className="sm:hidden" aria-label={t.editor.mobile.more}>
                <MoreHorizontal aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled={!history.future.length} onSelect={() => setHistory((h) => (h ? redo(h) : h))}>
                <Redo2 aria-hidden="true" />
                {t.editor.redo}
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`/situs/${id}/pratinjau`}>
                  <Eye aria-hidden="true" />
                  {t.editor.mobile.fullPreview}
                </Link>
              </DropdownMenuItem>
              {live && url ? (
                <DropdownMenuItem asChild>
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink aria-hidden="true" />
                    {t.editor.mobile.openLive}
                  </a>
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem disabled={!dirty || save.isPending} onSelect={() => void saveNow().catch(() => undefined)}>
                <Save aria-hidden="true" />
                {t.editor.mobile.saveNow}
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`/situs/${id}/riwayat`}>
                  <HistoryIcon aria-hidden="true" />
                  {t.editor.versions}
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {live && !pending ? (
            // Nothing new to publish: still reachable (republish), but no longer the loudest button.
            <Button size="sm" variant="secondary" onClick={() => setPublishOpen(true)}>
              <Check aria-hidden="true" />
              {t.editor.upToDate}
            </Button>
          ) : (
            <Button size="sm" onClick={() => setPublishOpen(true)}>
              <Rocket aria-hidden="true" />
              {t.editor.publish}
            </Button>
          )}
        </div>
      </header>
      <VerifyEmailBanner />

      {showBanner ? (
        <div className="flex shrink-0 items-start gap-2 border-b border-amber-200/80 bg-amber-50 py-1.5 pr-1 pl-3 text-[13px] leading-snug text-amber-950 sm:items-center sm:py-2 sm:pl-4">
          <span className="mt-[7px] size-1.5 shrink-0 rounded-full bg-amber-500 sm:mt-0" aria-hidden="true" />
          <button type="button" className="min-w-0 flex-1 py-0.5 text-left sm:pointer-events-none" onClick={() => setBannerOpen((o) => !o)} aria-expanded={bannerOpen}>
            <span className={cn("block", !bannerOpen && "max-sm:truncate")}>
              <span className="font-semibold">{!live && url ? t.editor.liveBar.draftTitle : t.editor.liveBar.pendingTitle}.</span>{" "}
              {!live && url ? t.editor.liveBar.draftBody(displayUrl(url)) : t.editor.liveBar.pendingBody}
            </span>
          </button>
          {!bannerOpen ? <ChevronDown className="mt-[3px] size-4 shrink-0 text-amber-700 sm:hidden" aria-hidden="true" /> : null}
          <button
            type="button"
            className="grid size-7 shrink-0 place-items-center rounded-full text-amber-800 hover:bg-amber-100"
            aria-label={t.editor.mobile.dismissBanner}
            onClick={() => {
              setBannerHidden(true);
              session.set(bannerKey, true);
            }}
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
      ) : null}

      <div ref={setBody} className="relative flex min-h-0 flex-1">
        {sheet && sheetSize === "full" ? (
          <button type="button" className="fixed inset-0 z-30 bg-slate-950/40 lg:hidden" aria-label={t.common.close} onClick={() => setSheet(null)} />
        ) : null}
        {!outlineDocked && outlineOverlay ? (
          <button type="button" className="absolute inset-0 z-30 bg-slate-950/10 max-lg:hidden" aria-label={t.common.close} onClick={() => setOutlineOverlay(false)} />
        ) : null}
        <aside
          className={cn(
            "z-40 flex min-h-0 w-[272px] shrink-0 flex-col border-r border-line bg-white",
            !outlineDocked && (outlineOverlay ? "lg:absolute lg:inset-y-0 lg:left-0 lg:shadow-2xl" : "lg:hidden"),
            sheetClasses(sheet === "outline", sheetSize),
          )}
        >
          <SheetHandle title={t.editor.outline.title} size={sheetSize} onSize={setSheetSize} onClose={() => setSheet(null)} />
          <div className="min-h-0 flex-1 max-lg:pt-2">{outline}</div>
        </aside>
        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="flex h-14 shrink-0 items-center gap-2 px-3 max-sm:hidden sm:px-4">
            {!outlineDocked ? (
              <Button variant="secondary" size="sm" className="shrink-0 max-lg:hidden" onClick={() => setOutlineOverlay(true)} aria-expanded={outlineOverlay}>
                <Layers aria-hidden="true" />
                {t.editor.outline.short}
              </Button>
            ) : null}
            {textTarget && theme && !touch && !previewMode ? (
              <TextToolbar
                key={`toolbar:${textTarget.sectionId}:${textTarget.path}`}
                placement="chrome"
                def={def}
                target={textTarget}
                theme={theme}
                apply={apply}
                onClose={() => setTextTarget(null)}
                count={inline ? { used: (inlineValue ?? "").length, max: inline.spec.max } : undefined}
                hint={inline ? (inline.spec.multiline ? t.editor.inline.hintMulti : t.editor.inline.hint) : undefined}
              />
            ) : (
              <p className="hidden min-w-0 flex-1 truncate text-xs font-medium text-slate-500 sm:block">
                {t.editor.canvasHint}
              </p>
            )}
            <p className="sr-only" aria-live="polite">
              {inline ? `${t.editor.inline.editing}. ${inline.spec.multiline ? t.editor.inline.hintMulti : t.editor.inline.hint}. ${(inlineValue ?? "").length}/${inline.spec.max}` : t.editor.canvasHint}
            </p>
            <div className={cn("ml-auto shrink-0", phoneOnly && "lg:hidden")}>
              <Segmented
                size="sm"
                label={t.editor.deviceLabel}
                value={device}
                onChange={setDevice}
                options={[
                  { value: "mobile", label: <Smartphone aria-hidden="true" />, ariaLabel: t.editor.device.mobile },
                  { value: "tablet", label: <Tablet aria-hidden="true" />, ariaLabel: t.editor.device.tablet },
                  { value: "desktop", label: <Monitor aria-hidden="true" />, ariaLabel: t.editor.device.desktop },
                ]}
              />
            </div>
          </div>
          <div
            className={cn(
              "scrollbar-thin min-h-0 flex-1 overflow-auto [overflow-anchor:none] bg-[radial-gradient(circle_at_1px_1px,rgb(148_163_184/0.45)_1px,transparent_0)] bg-[length:18px_18px] sm:px-6",
              // Phones: end above the fixed bottom nav, so the site's own sticky bottom menu stays visible.
              "max-lg:mb-[calc(3.5rem+env(safe-area-inset-bottom))]",
              dragWidth !== null && "pointer-events-none",
            )}
          >
            {theme ? (
              // On phones the canvas is the phone: no bezel or margins, the site uses the full width.
              <DeviceFrame device={phoneOnly ? "mobile" : device} flushOnPhone>
                {/* Browser chrome reads as "a website" on larger screens; on a phone the canvas already is one. */}
                <div className="flex items-center gap-2 border-b border-slate-200/80 bg-slate-50 px-3 py-2 max-sm:hidden">
                  <span className="flex gap-1" aria-hidden="true">
                    <i className="size-2 rounded-full bg-slate-300" />
                    <i className="size-2 rounded-full bg-slate-300" />
                    <i className="size-2 rounded-full bg-slate-300" />
                  </span>
                  <span className="mx-auto flex max-w-[70%] items-center gap-1.5 truncate rounded-full bg-white px-3 py-1 text-[11px] font-medium text-slate-500 ring-1 ring-slate-900/5">
                    {url ? displayUrl(url) : page.title}
                    <Badge tone="neutral" className="h-5 shrink-0 px-1.5 text-[10px]">{t.editor.preview}</Badge>
                  </span>
                </div>
                <div ref={setCanvas} className="baze-canvas relative" data-preview={previewMode ? "" : undefined} onMouseOver={onCanvasOver} onMouseLeave={clearHover} onMouseDown={onCanvasDown} onClickCapture={onCanvasClick}>
                  {/* Pratinjau renders what visitors get (live mode, enhancers running); no id prefix so #anchors resolve. */}
                  <WebsiteRenderer definition={def} theme={theme} pageSlug={page.slug} mode={previewMode ? "live" : "editor"} framed siteId={id} branding={site.data ? !site.data.plan.limits.removeBranding || site.data.keepBranding : true} omitSections={blog ? undefined : ["blog-latest"]} products={storePreview.products} store={storePreview.store} selectedSectionId={previewMode ? null : selected} idPrefix={previewMode ? "" : "ed-"} />
                  {previewMode ? <SiteRuntime page={page} runKey={page.id} /> : null}
                  {inline && canvasEl && !touch ? (
                    <InlineTextEditor
                      key={`${inline.sectionId}:${inline.path}`}
                      root={canvasEl}
                      target={inline}
                      value={inlineValue ?? ""}
                      onChange={(v) => apply(updateContent(def, inline.sectionId, inline.path, v), `${inline.sectionId}:${inline.path}`)}
                      onDone={() => setInline(null)}
                      onCancel={() => { setInline(null); setTextTarget(null); }}
                      onTab={moveInline}
                    />
                  ) : null}
                </div>
              </DeviceFrame>
            ) : (
              <Skeleton className="h-96 rounded-2xl" />
            )}
            {/* Room to scroll the last section above whatever covers the bottom (sheet, action bar, nav).
                A spacer, not padding: sticky site chrome (bottom menu, WhatsApp button) sticks to the
                scroller's padding edge, so padding would leave it floating above the canvas bottom. */}
            <div aria-hidden="true" className={cn(sheet ? "h-[60dvh]" : showActionBar ? "h-44" : inline && touch ? "h-[50dvh]" : "h-20", "lg:h-8")} />
          </div>
        </main>
        <aside
          className={cn(
            "z-40 flex min-h-0 shrink-0 flex-col border-l border-line bg-white lg:relative lg:w-(--inspector-w)",
            // Eases the switch between panels; a drag follows the pointer directly.
            dragWidth === null && measured && "lg:motion-safe:transition-[width] lg:duration-200 lg:ease-out",
            sheetClasses(sheet === "inspect", sheetSize),
          )}
          style={{ ["--inspector-w" as string]: `${panelWidth}px` }}
        >
          {isDesktop ? (
            <ResizeHandle
              width={panelWidth}
              min={minWidth}
              max={dragMax}
              onResize={setDragWidth}
              onCommit={commitWidth}
              onReset={() => {
                setDragWidth(null);
                storeWidth(wide ? "wide" : "normal", wide ? 0 : NORMAL_WIDTH);
              }}
            />
          ) : null}
          <SheetHandle title={section ? typeLabel(section.type) : t.editor.tabs.info} size={sheetSize} onSize={setSheetSize} onClose={() => setSheet(null)} />
          <div className="min-h-0 flex-1">{inspector}</div>
        </aside>
      </div>

      <AddSectionSheet open={sheet === "add"} page={page} size={sheetSize} suggested={suggested} event={event} blog={blog} onSize={setSheetSize} onClose={() => setSheet(null)} onAdd={addOfType} />

      {showActionBar && section ? (
        <SectionActionBar
          type={section.type}
          hidden={!!section.hidden}
          pinned={section.type === "navbar" || section.type === "footer"}
          canUp={moveSection(def, section.id, -1) !== def}
          canDown={moveSection(def, section.id, 1) !== def}
          onUp={() => apply(moveSection(def, section.id, -1))}
          onDown={() => apply(moveSection(def, section.id, 1))}
          onDuplicate={() => duplicate(section.id)}
          onHide={() => apply(setHidden(def, section.id, !section.hidden))}
          onDelete={() => deleteSection(section.id)}
          onEdit={editSelected}
          onClose={() => setSelected(null)}
        />
      ) : null}

      {inline && touch && inlineSection ? (
        <DockedTextEditor
          key={`${inline.sectionId}:${inline.path}`}
          target={inline}
          sectionType={inlineSection.type}
          value={inlineValue ?? ""}
          onChange={(v) => apply(updateContent(def, inline.sectionId, inline.path, v), `${inline.sectionId}:${inline.path}`)}
          onDone={() => setInline(null)}
          onNext={() => moveInline(1)}
          styleBar={theme ? <TextToolbar placement="dock" def={def} target={{ sectionId: inline.sectionId, path: inline.path }} theme={theme} apply={apply} onClose={() => undefined} /> : null}
        />
      ) : null}

      {previewMode ? (
        <button
          type="button"
          onClick={() => setPreviewMode(false)}
          className="fixed top-[calc(4rem+env(safe-area-inset-top))] left-1/2 z-40 flex h-10 -translate-x-1/2 items-center gap-2 rounded-full whitespace-nowrap bg-slate-900/90 px-4 text-xs font-semibold text-white shadow-lg backdrop-blur lg:hidden"
        >
          <Eye className="size-4" aria-hidden="true" />
          {t.editor.mobile.previewing} · <span className="underline underline-offset-2">{t.editor.mobile.backToEdit}</span>
        </button>
      ) : null}

      <nav className="safe-bottom fixed inset-x-0 bottom-0 z-50 grid grid-cols-4 border-t border-line bg-white lg:hidden" aria-label={t.editor.mobile.nav}>
        <NavButton active={sheet === "outline"} label={t.editor.mobile.sections} onClick={() => { setPreviewMode(false); setSheet((s) => (s === "outline" ? null : "outline")); }}>
          <Layers aria-hidden="true" />
        </NavButton>
        <NavButton active={sheet === "add"} label={t.editor.mobile.add} primary onClick={() => { setPreviewMode(false); setInline(null); setTextTarget(null); setSheet((s) => (s === "add" ? null : "add")); }}>
          <Plus aria-hidden="true" />
        </NavButton>
        <NavButton active={sheet === "inspect"} label={t.editor.mobile.edit} onClick={() => { setPreviewMode(false); setSheet((s) => (s === "inspect" ? null : "inspect")); }}>
          <PenLine aria-hidden="true" />
        </NavButton>
        <NavButton
          active={previewMode}
          label={t.editor.mobile.preview}
          onClick={() => {
            setSheet(null);
            setInline(null);
            setTextTarget(null);
            clearHover();
            setPreviewMode((p) => !p);
          }}
        >
          <Eye aria-hidden="true" />
        </NavButton>
      </nav>

      <PublishDialog
        open={publishOpen || publishFromUrl}
        onOpenChange={(next) => {
          setPublishOpen(next);
          if (!next && publishFromUrl) {
            const cleared = new URLSearchParams(params);
            cleared.delete("publish");
            setParams(cleared, { replace: true });
          }
        }}
        siteId={id}
        dirty={dirty}
        save={saveNow}
        upToDate={live && !pending}
        onFix={(issue) => {
          if (issue.pageId) setPageId(issue.pageId);
          if (issue.sectionId) select(issue.sectionId, true);
          else if (issue.code === "whatsapp_missing") openInfo();
          setPublishOpen(false);
        }}
      />
    </div>
  );
}

function NavButton({ active, primary, label, onClick, children }: { active: boolean; primary?: boolean; label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn("flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold [&_svg]:size-5", active ? "text-primary" : "text-slate-600")}
    >
      {/* Same-height icon slot in every column so the labels share one baseline. */}
      <span className="grid h-7 place-items-center">
        {primary ? (
          <span className={cn("grid size-7 place-items-center rounded-full shadow-sm transition-transform [&_svg]:size-4", active ? "rotate-45 bg-slate-900 text-white" : "bg-primary text-white")}>{children}</span>
        ) : (
          children
        )}
      </span>
      {label}
    </button>
  );
}

function typeLabel(type: SectionType) {
  return SECTION_META[type].label;
}
